import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const [favorited, setFavorited] = useState(false);

  const related = useMemo(() => {
    if (!movie) return [];
    const sameCategory = movies.filter((m) => m.id !== movie.id && m.category === movie.category);
    const sameType = movies.filter(
      (m) => m.id !== movie.id && m.type === movie.type && m.category !== movie.category,
    );
    return [...sameCategory, ...sameType].slice(0, 6);
  }, [movie]);

  useSeo({
    title: movie
      ? `${movie.title} - 剧情介绍与演职员 - 恋爱的味道`
      : "作品未找到 - 恋爱的味道",
    description: movie
      ? `${movie.title}讲述了动人的爱情故事，主演${movie.actors.join("、")}及详细剧情介绍，尽在恋爱的味道影视站。`
      : "抱歉，未找到该影视作品，请返回首页继续浏览。",
    keywords: movie ? `${movie.title},${movie.category},${movie.region},剧情介绍,恋爱的味道` : undefined,
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
          <EmptyState
            title="未找到该影视作品"
            desc="该作品可能已下架或链接有误，您可以返回首页继续发现心仪的爱情影视。"
            actionLabel="返回首页"
            onAction={() => navigate("/")}
          />
        </div>
      </SiteLayout>
    );
  }

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "首播年份", value: `${movie.year} 年` },
    { label: "剧集数", value: movie.episodes > 0 ? `${movie.episodes} 集` : "电影" },
    { label: "更新状态", value: movie.updateStatus },
  ];

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-600">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link
            to={`/category/${movie.type}`}
            className="transition-colors hover:text-primary-600"
          >
            {movie.type}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{movie.title}</span>
        </nav>

        <div className="mt-6 flex flex-col gap-6 rounded-lg border border-background-200 bg-white p-5 md:flex-row md:gap-8 md:p-6">
          <div className="mx-auto w-[220px] shrink-0 md:mx-0 md:w-[300px]">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-100">
              <img
                src={movie.cover}
                alt={`${movie.title} 封面大图`}
                title={`${movie.title} - ${movie.category}封面`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${movie.type}`}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                {movie.type}
              </Link>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                {movie.category}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                {movie.region}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>
            <p className="mt-1 text-sm text-foreground-600">
              {movie.type} · {movie.category} · {movie.year} 年
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-3xl font-bold text-primary-600">
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-sm text-foreground-500">/ 10 分</span>
              </div>
              <div className="h-8 w-px bg-background-200"></div>
              <div>
                <p className="text-sm text-foreground-600">
                  热度 <strong className="font-semibold text-accent-600">{movie.views} 万</strong>
                </p>
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="min-w-0 flex-1 text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setFavorited((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-background-50"
                    : "border border-background-300 text-foreground-800 hover:bg-background-100"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏本片"}
              </button>
              <Link
                to={`/category/${movie.type}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
              >
                <i className="ri-play-list-line"></i>
                同类型作品
              </Link>
            </div>
            <p className="mt-2 text-xs text-foreground-500">
              * 收藏为前端演示状态，不会保存到账号或服务器
            </p>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-file-text-line text-base"></i>
            </span>
            剧情简介
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
              <i className="ri-sparkling-2-line text-base"></i>
            </span>
            情感亮点与看点
          </h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {movie.highlights.map((item, i) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-white p-4"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-100 text-xs font-bold text-primary-700">
                  {i + 1}
                </span>
                <span className="text-sm leading-relaxed text-foreground-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-movie-line text-base"></i>
            </span>
            相关推荐
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}